import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { MagnifyingGlass, User, CaretDown, MapPin, ShieldCheck, SignOut, Tag, ShoppingCart, Heart } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";
import AdminNotifications from "@/components/AdminNotifications";
import UserNotifications from "@/components/UserNotifications";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: favCount } = useFavorites();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.filter((c) => !c.parent_id))).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    navigate(q ? `/?q=${encodeURIComponent(q)}` : "/");
  };

  return (
    <header className="bg-[#2D3277] sticky top-0 z-50 shadow-md">
      <div className="max-w-[1200px] mx-auto px-3 sm:px-5 pt-3 flex items-center gap-3 sm:gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <img src="/todito-logo.png" alt="Todito Ventas" className="h-14 sm:h-16 w-auto drop-shadow-sm" />
        </Link>

        <form onSubmit={submit} className="flex-1 max-w-2xl relative">
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar productos..."
            className="w-full h-10 rounded-md bg-white pl-4 pr-11 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E8662B]/40 border border-black/5"
          />
          <button type="submit" data-testid="search-button"
                  className="absolute right-0 top-0 h-10 w-11 flex items-center justify-center border-l border-gray-200 text-[#2D3277] hover:text-[#E8662B]">
            <MagnifyingGlass weight="bold" size={18} />
          </button>
        </form>

        <div className="flex items-center gap-3 shrink-0">
          {user?.is_admin && <AdminNotifications />}
          {user && !user.is_admin && <UserNotifications />}
          <Link to="/favoritos" data-testid="fav-link" className="relative text-white hover:text-[#E8662B] transition-colors hidden sm:block">
            <Heart weight="bold" size={23} />
            {favCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#E8662B] text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">{favCount}</span>}
          </Link>
          <Link to="/carrito" data-testid="cart-link" className="relative text-white hover:text-[#E8662B] transition-colors">
            <ShoppingCart weight="bold" size={24} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1.5 -right-1.5 bg-[#E8662B] text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">{count}</span>
            )}
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 text-[13px] text-white font-medium focus:outline-none">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center"><User weight="bold" size={16} /></span>
                )}
                <span className="hidden sm:inline max-w-[110px] truncate">{user.name.split(" ")[0]}</span>
                <CaretDown weight="bold" size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => navigate("/cuenta")} data-testid="menu-account">
                  <User className="mr-2" size={16} /> Mi cuenta
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favoritos")} data-testid="menu-favorites">
                  <Heart className="mr-2" size={16} /> Mis favoritos
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="mr-2" size={16} /> Administración
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-destructive">
                  <SignOut className="mr-2" size={16} /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/cuenta" data-testid="header-login-button"
                    className="text-[13px] font-semibold text-white hover:text-[#E8662B] transition-colors whitespace-nowrap">
              Ingresar
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-3 sm:px-5 h-9 flex items-center gap-4 sm:gap-6 text-[13px] text-white/90 overflow-x-auto no-scrollbar">
        <span className="hidden md:flex items-center gap-1 text-white/70 shrink-0">
          <MapPin weight="fill" size={15} /> Envíos a todo el país
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="nav-categories" className="flex items-center gap-1 font-medium hover:text-[#E8662B] focus:outline-none shrink-0">
            Categorías <CaretDown weight="bold" size={11} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
            {categories.map((c) => (
              <DropdownMenuItem key={c.id} onClick={() => navigate(`/?category=${c.id}`)} data-testid={`menu-cat-${c.name}`}>
                <Tag className="mr-2" size={14} /> {c.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Link to="/?on_offer=1" data-testid="nav-ofertas" className="font-medium hover:text-[#E8662B] shrink-0">Ofertas</Link>
        {user?.is_admin && (
          <Link to="/admin" data-testid="nav-admin" className="font-medium hover:text-[#E8662B] shrink-0">Administración</Link>
        )}
      </div>
    </header>
  );
}
